import { useState, useRef, useEffect } from "react";
import { Share2, X, Link2, Check, Loader2, Twitter, Facebook } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Builds a public share record on demand and offers social-share targets that
// link back to the app (a branded, crawler-friendly page at /api/s/:id).
export function ShareMenu({ payload, label = "Share", variant = "primary", iconOnly = false, testId = "share" }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrlRef = useRef(null);
  const idRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const ensureShare = async () => {
    if (shareUrlRef.current) return shareUrlRef.current;
    const { data } = await api.post("/shares", payload);
    idRef.current = data.id;
    const url = `${window.location.origin}/api/s/${data.id}`;
    shareUrlRef.current = url;
    return url;
  };

  const shareText = () => {
    const first = payload.decrees?.[0];
    const t = payload.topic ? `Decreeing "${payload.topic}" — ` : "";
    return `${t}${first ? `"${first.text}" (${first.reference})` : "Powerful scripture decrees"} · It Is Established`;
  };

  const openMenu = async () => {
    setOpen(true);
    if (shareUrlRef.current) return;
    setCreating(true);
    try {
      const url = await ensureShare();
      // Prefer the native share sheet on devices that support it.
      if (navigator.share) {
        try {
          await navigator.share({ title: "It Is Established", text: shareText(), url });
          setOpen(false);
        } catch (_) { /* user dismissed — keep the fallback menu open */ }
      }
    } catch (e) {
      toast.error("Could not create a share link. Please try again.");
      setOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const openWindow = (url) => window.open(url, "_blank", "noopener,noreferrer,width=600,height=640");

  const toTwitter = () => {
    const url = shareUrlRef.current;
    openWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText())}&url=${encodeURIComponent(url)}`);
  };
  const toFacebook = () => {
    openWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrlRef.current)}`);
  };
  const toWhatsApp = () => {
    openWindow(`https://wa.me/?text=${encodeURIComponent(`${shareText()} ${shareUrlRef.current}`)}`);
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrlRef.current);
    setCopied(true);
    toast.success("Share link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const btnBase =
    "flex items-center gap-2 px-4 py-2.5 text-sm transition-all duration-300 rounded-sm";
  const trigger =
    variant === "primary"
      ? `${btnBase} bg-[#2C2A29] text-[#FAF9F6] hover:opacity-90`
      : `${btnBase} border border-[#D9D4CC] text-[#2C2A29] hover:bg-[#EAE6DF]`;

  return (
    <div className="relative inline-block" ref={panelRef}>
      <button
        data-testid={`${testId}-open`}
        onClick={openMenu}
        className={iconOnly ? "text-[#B7AE9F] hover:text-[#2C2A29] transition-colors duration-300 p-1" : trigger}
        aria-label={label}
      >
        <Share2 className="w-4 h-4" />
        {!iconOnly && label}
      </button>

      {open && (
        <div
          data-testid={`${testId}-menu`}
          className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-sm border border-[#D9D4CC] bg-[#FDFBF7] shadow-lg p-2 animate-fade-up"
        >
          <div className="flex items-center justify-between px-2 py-1.5">
            <span className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E]">Share to</span>
            <button onClick={() => setOpen(false)} className="text-[#B7AE9F] hover:text-[#2C2A29]" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {creating ? (
            <div className="flex items-center gap-2 px-2 py-4 text-sm text-[#7A756E]">
              <Loader2 className="w-4 h-4 animate-spin" /> Preparing link…
            </div>
          ) : (
            <div className="space-y-0.5">
              <ShareRow testId={`${testId}-twitter`} onClick={toTwitter} icon={<Twitter className="w-4 h-4" />} labelText="X (Twitter)" />
              <ShareRow testId={`${testId}-facebook`} onClick={toFacebook} icon={<Facebook className="w-4 h-4" />} labelText="Facebook" />
              <ShareRow
                testId={`${testId}-whatsapp`}
                onClick={toWhatsApp}
                icon={
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M17.5 14.4c-.3-.15-1.7-.84-2-.94-.26-.1-.46-.15-.65.15-.2.3-.75.94-.92 1.13-.17.2-.34.22-.63.08-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.14.3-.34.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.65-1.57-.9-2.15-.24-.57-.48-.5-.65-.5h-.56c-.2 0-.5.07-.77.37-.26.3-1 1-1 2.42 0 1.43 1.03 2.8 1.17 3 .15.2 2.03 3.1 4.92 4.35.69.3 1.22.47 1.64.6.69.22 1.32.2 1.81.12.55-.08 1.7-.7 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.2-.56-.34zM12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.33A10 10 0 1 0 12 2z"/></svg>
                }
                labelText="WhatsApp"
              />
              <button
                data-testid={`${testId}-copy`}
                onClick={copyLink}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
              >
                {copied ? <Check className="w-4 h-4 text-[#9B7E4E]" /> : <Link2 className="w-4 h-4" />}
                Copy link
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ShareRow({ onClick, icon, labelText, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
    >
      {icon}
      {labelText}
    </button>
  );
}
